export type NavGroup = "main" | "admin";

export interface NavItem {
  to: string;
  labelKey: string;
  /** Which section the item renders under. Defaults to "main". */
  group?: NavGroup;
  visibleTo?: (ctx: { isAdmin: boolean; role: string; isPrincipal: boolean; isGuardian: boolean }) => boolean;
  /** Teacher-role spec §5.2: module key resolved by the backend matrix
   *  (org/me.modules). Items without a key are always shown. */
  moduleKey?: string;
}

interface VisibleNavOptions {
  isAdmin: boolean;
  role: string;
  /** 园长(school_teachers.role='admin')。后端早已按"集团管理员或园长"
   *  放行学校管理/审批队列等,侧边栏却只认 isAdmin —— 园长登录后整个
   *  「管理」分组不显示,功能等于不存在(Jarod 2026-08-28)。 */
  isPrincipal?: boolean;
  hideForRole: readonly string[];
  /** org/me.modules — three-state visible|readonly|hidden per module.
   *  undefined (not yet loaded / legacy) = no module filtering. */
  modules?: Record<string, string>;
  /** 钉钉家校通讯录里的家长 → 显示「孩子在校情况」。 */
  isGuardian?: boolean;
  /** 只是家长(无老师档案、非管理员)→ 只显示家长页。 */
  guardianOnly?: boolean;
}

const onlyAdmin = ({ isAdmin }: { isAdmin: boolean }) => isAdmin;
/** 集团管理员或园长 —— 与后端 is_school_manager 同一口径。 */
const adminOrPrincipal = ({ isAdmin, isPrincipal }: { isAdmin: boolean; isPrincipal?: boolean }) =>
  isAdmin || isPrincipal === true;

const NAV_ITEMS: readonly NavItem[] = [
  // ── Main: filtered per-teacher by the backend module matrix (§5.2) ──
  { to: "/", labelKey: "nav.chat", moduleKey: "workbench" },
  { to: "/parent", labelKey: "nav.parent", visibleTo: ({ isGuardian }) => isGuardian },
  { to: "/classes", labelKey: "nav.classes", moduleKey: "classes" },
  { to: "/admissions", labelKey: "nav.admissions", moduleKey: "admissions" },
  { to: "/itarget", labelKey: "nav.itarget", moduleKey: "itarget" },
  { to: "/assignments", labelKey: "nav.assignments", moduleKey: "assignments" },
  { to: "/exams", labelKey: "nav.exams", moduleKey: "exams" },
  { to: "/reports", labelKey: "nav.reports", moduleKey: "reports" },
  { to: "/teaching-plans", labelKey: "nav.teachingPlans", moduleKey: "teaching_plans" },
  { to: "/curriculum", labelKey: "nav.curriculum", moduleKey: "teaching_plans" },
  { to: "/documents", labelKey: "nav.documents", moduleKey: "documents" },
  { to: "/my-promotions", labelKey: "nav.myPromotions", moduleKey: "my_promotions" },

  // ── Admin (principal) only: the management dashboard ──
  // Subjects = create subject + invite teachers + manage the shared
  // knowledge collection. Approval queue = review teacher file uploads
  // and promote them to the global "brain".
  // 集团知识库(院所信息)—— 园长管理文件;老师不进这个页面,他们在聊天里
  // 检索得到内容但拿不到文件(Jarod 2026-09-01 定的口径)。
  { to: "/library", labelKey: "nav.library", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/subjects", labelKey: "nav.subjects", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/gpa/overview", labelKey: "nav.gpaOverview", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/gpa/config", labelKey: "nav.gpaConfig", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/admin/schools", labelKey: "nav.orgSchools", group: "admin", visibleTo: adminOrPrincipal },
  // 2026-09-15:老师也要能看到知识点管理(自己提的候选在哪、审核到哪一步),
  // 页面内的审核/合并/停用按钮仍只给园长/管理员(canManage)。此前老师在
  // 「添加单元」里被提示去「知识点管理」却根本找不到入口(Jarod)。
  { to: "/kp", labelKey: "nav.kp", group: "admin" },
  { to: "/admin/promotions", labelKey: "nav.promotionQueue", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/admin/invitations", labelKey: "nav.invitations", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/admin/dingtalk", labelKey: "nav.dingtalk", group: "admin", visibleTo: adminOrPrincipal },
  { to: "/admin/access-logs", labelKey: "nav.accessLogs", group: "admin", visibleTo: onlyAdmin },
];

export function getVisibleNavItems({ isAdmin, role, hideForRole, modules, isPrincipal, isGuardian, guardianOnly }: VisibleNavOptions): NavItem[] {
  if (guardianOnly) return NAV_ITEMS.filter((n) => n.to === "/parent");
  return NAV_ITEMS.filter(
    (n) => !n.visibleTo || n.visibleTo({ isAdmin, role, isPrincipal: isPrincipal === true, isGuardian: isGuardian === true }),
  )
    .filter((n) => !hideForRole.some((prefix) => n.to === prefix || n.to.startsWith(prefix + "/")))
    .filter((n) => {
      // 隐藏 ≠ 无权限:这里只是 UX 过滤,后端各端点仍按 scope 强制鉴权。
      if (!modules || isAdmin || !n.moduleKey) return true;
      return modules[n.moduleKey] !== "hidden";
    });
}
